// src/systems/boss.ts
//
// Boss-round modifier picker, descriptions, and bet-placement gate.
// Pure module: imports only types and rng helpers. RoundScene wires this in.

import type { BossModifier, BossModifierLeaf } from '../state/types';
import { pickFromArray, type Rng } from './rng';

export const BOSS_MODIFIER_LEAVES: readonly BossModifierLeaf[] = Object.freeze([
  { type: 'noEvenMoney' },
  { type: 'noStraights' },
  { type: 'redHalf' },
  { type: 'blackHalf' },
  { type: 'zeroFlood' },
  { type: 'cellBlocker' },
  { type: 'nudge' },
] as const);

// Legacy alias retained for callers that imported BOSS_MODIFIERS pre-rewrite.
export const BOSS_MODIFIERS = BOSS_MODIFIER_LEAVES;

/** Returns the leaf modifier(s) active for a given boss. Devil expands to its 2 sub-mods. */
export function getEffectiveMods(mod: BossModifier | null | undefined): BossModifierLeaf[] {
  if (!mod) return [];
  if (mod.type === 'devil') return [...mod.mods];
  return [mod];
}

export function pickBossModifier(rng: Rng): BossModifier {
  return pickFromArray(rng, BOSS_MODIFIER_LEAVES);
}

/** Pick the devil boss: 2 distinct random leaf mods. Used at ante 3. */
export function pickDevilBoss(rng: Rng): BossModifier {
  const a = pickFromArray(rng, BOSS_MODIFIER_LEAVES);
  const rest = BOSS_MODIFIER_LEAVES.filter((m) => m.type !== a.type);
  const b = pickFromArray(rng, rest);
  return { type: 'devil', mods: [a, b] };
}

/**
 * Returns a Set<number> of 10 wheel slot indices that are flooded with 0 this spin.
 * Call once per spin with the run RNG so the result is deterministic.
 */
export function computeFloodedIndices(rng: Rng, wheelLength: number): Set<number> {
  const count = Math.min(10, wheelLength);
  const indices = new Set<number>();
  while (indices.size < count) {
    indices.add(Math.floor(rng() * wheelLength));
  }
  return indices;
}

/**
 * Returns an array of flat cell indices to block for the cellBlocker boss.
 * Call once at round entry.
 */
export function pickBlockedCellIndices(rng: Rng, cellCount: number): number[] {
  const count = Math.min(10, cellCount);
  const picked = new Set<number>();
  while (picked.size < count) {
    picked.add(Math.floor(rng() * cellCount));
  }
  return [...picked];
}

export function describeBossModifier(mod: BossModifier): { title: string; subtitle: string } {
  switch (mod.type) {
    case 'noEvenMoney':
      return {
        title: 'NO EVEN-MONEY',
        subtitle: 'Red, Black, Even, Odd, Low, High sub-cells are blocked.',
      };
    case 'noStraights':
      return {
        title: 'NO STRAIGHTS',
        subtitle: 'Single-number bets are blocked.',
      };
    case 'redHalf':
      return {
        title: 'RED HALF',
        subtitle: 'Red bets pay half. Bleached cells count as both red and black.',
      };
    case 'blackHalf':
      return {
        title: 'BLACK HALF',
        subtitle: 'Black bets pay half. Bleached cells count as both red and black.',
      };
    case 'zeroFlood':
      return {
        title: 'ZERO FLOOD',
        subtitle: '10 random wheel slots act as 0 every spin.',
      };
    case 'cellBlocker':
      return {
        title: 'CELL BLOCKER',
        subtitle: '10 random board cells are blocked. No fiches, no chess lines.',
      };
    case 'nudge':
      return {
        title: 'THE NUDGE',
        subtitle:
          'After landing, the ball shifts to whichever of the 3 nearest slots scores lowest.',
      };
    case 'devil': {
      const names = mod.mods.map((m) => describeBossModifier(m).title).join(' + ');
      return { title: 'THE DEVIL', subtitle: names };
    }
  }
}

export function isBetBlockedByBoss(mod: BossModifier | undefined | null, betKind: string): boolean {
  for (const m of getEffectiveMods(mod)) {
    if (
      m.type === 'noEvenMoney' &&
      ['red', 'black', 'even', 'odd', 'low', 'high', 'outside-sub'].includes(betKind)
    ) {
      return true;
    }
    if (m.type === 'noStraights' && betKind === 'straight') return true;
  }
  return false;
}
